/**
 * Generic GET {apiBasePath}/separation worklist client (Brazil and future sources).
 */
import makeRequest from './client';
import { HttpStatus } from './httpStatus';
import {
  SeparationWorklistParams,
  SeparationWorklistResult
} from '../components/Common/SeparationWorklist/types';

const DEFAULT_ERROR_MESSAGE = 'Failed to fetch worklist';

/** Build query string for GET /separation (stage, pagination, optional search and qc_status). */
export const getSeparationWorklist = (apiBasePath: string, params: SeparationWorklistParams) => {
  const queryParams = [
    `stage=${params.stage}`,
    `page=${params.pageNo}`,
    `page_size=${params.pageSize}`
  ];

  if (params.search) {
    queryParams.push(`search=${encodeURIComponent(params.search)}`);
  }

  if (params.qcStatus && params.qcStatus !== 'All') {
    queryParams.push(`qc_status=${encodeURIComponent(params.qcStatus)}`);
  }

  return makeRequest(`${apiBasePath}/separation?${queryParams.join('&')}`, 'GET');
};

const extractErrorMessage = (response: any): string => {
  const message = response?.data?.message;
  if (typeof message === 'string' && message.trim()) {
    return message;
  }

  const statusLabel = response?.data?.status;
  if (typeof statusLabel === 'string' && statusLabel.trim()) {
    return statusLabel;
  }

  if (response?.status) {
    return `${DEFAULT_ERROR_MESSAGE} (${response.status})`;
  }

  return DEFAULT_ERROR_MESSAGE;
};

/** Parse worklist HTTP response; 404 is treated as an empty list, not an error. */
export const resolveSeparationWorklistResponse = (response: any): SeparationWorklistResult => {
  if (!response) {
    return {
      ok: false,
      message: 'Unable to reach the server. Please try again.'
    };
  }

  const successBody = response?.data?.Success;
  if (successBody?.data !== undefined) {
    return {
      ok: true,
      data: successBody.data,
      count: successBody.count ?? 0
    };
  }

  if (response.status === HttpStatus.NOT_FOUND) {
    return { ok: true, data: [], count: 0 };
  }

  if (response.data?.error === true || (response.status && response.status >= 400)) {
    return { ok: false, message: extractErrorMessage(response) };
  }

  return { ok: false, message: DEFAULT_ERROR_MESSAGE };
};
